import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-expense/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'

// helpers
import Axios from '../../helpers/axios'
import { 
  singleProjectsOptionsSelect, 
  singleExpenseIntervalOptionSelect,
  singleExpenseCategoryOptionsSelect,
  singleExpenseTagsOptionsSelect,
  singleExpenseCategoryAirplaneOptionSelect,
  singleExpenseCategoryTransportOptionSelect,
  singleExpenseCategoryFuelOptionSelect,
  singleExpenseCategoryTripTypeOptionSelect,
  singleExpenseCategoryFlightOptionSelect,
  singleExpenseCategoryTrainOptionSelect,
  singleExpenseCategoryAccomodationOptionSelect
} from '../../helpers/format-options'
import { capitalizeFirstLetter } from '../../helpers/text-format'
import useToastMessage from 'src/helpers/toast-message'

const DuplicateExpense = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  const params = useParams()
  const { id } = params

  const isLoading = useSelector((state) => state.isLoading)

  const [expenseDetail, setExpenseDetail] = useState('')
  const [projectOptions, setProjectOptions] = useState([])
  const [projectValue, setProjectValue] = useState(null)

  const [expenseCategoryOptions, setExpenseCategoryOptions] = useState([])
  const [expenseCategoryValue, setExpenseCategoryValue] = useState(null)
  const [expenseAirplaneValue, setExpenseAirplaneValue] = useState(null)
  const [expenseTransportationValue, setExpenseTransportationValue] = useState(null)
  const [expenseFuelValue, setExpenseFuelValue] = useState(null)
  const [expenseFlightValue, setExpenseFlightValue] = useState(null)
  const [expenseTrainValue, setExpenseTrainValue] = useState(null)
  const [expenseAccomodationValue, setExpenseAccomodationValue] = useState(null)
  const [expenseAccomodationProvinceValue, setExpenseAccomodationProvinceValue] = useState(null)
  const [expenseAccomodationCitiesValue, setExpenseAccomodationCitiesValue] = useState(null)
  const [expenseTagsOptions, setExpenseTagsOptions] = useState([])

  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [intervalValue, setIntervalValue] = useState({
    label: 'No repeat',
    value: 'no repeat'
  })

  const [defaultCountryIdIndonesia, setDefaultCountryIdIndonesia] = useState('104')

  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {} })
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const formik = useFormik({
    initialValues: {
      expense_name: expenseDetail?.name,
      expense_description: expenseDetail?.description,
      expense_value: expenseDetail?.value,
      expense_date: expenseDetail?.date,
      expense_repeat_start_date: expenseDetail?.repeat_start_date || '',
      expense_repeat_end_date: expenseDetail?.repeat_end_date || '',
      expense_repeat_forever: expenseDetail?.repeat_forever,
      expense_repeat_interval: expenseDetail?.repeat_interval,
      expense_notes: expenseDetail?.notes || '',
      project_id: expenseDetail?.project_id || '',
      is_related_to_project: expenseDetail?.is_related_to_project,
      file: expenseDetail?.attachment_url ? [{attachment_url: expenseDetail.attachment_url, attachment_original_name: expenseDetail?.attachment_original_name}] : [],
      category_id: expenseDetail?.category_id || '',
      category_value: expenseDetail?.category_value || null,
      expense_flight: {
        reservation_number: expenseDetail?.flight?.reservation_number || "",
        airplane_name:  expenseDetail?.flight?.airplane_name || "",
        flight_details: expenseDetail?.flight?.flight_details || "",
        flight_type: expenseDetail?.flight?.flight_type || "", // One Way / Return
        reservation_date_1: expenseDetail?.flight?.reservation_date_1 || "",
        reservation_date_2: expenseDetail?.flight?.reservation_date_2 || "",
        booking_through: expenseDetail?.flight?.booking_through || "",
        other_booking_input: expenseDetail?.flight?.other_booking_input || "",
        passengers: expenseDetail?.flight?.passengers?.length > 0 ? expenseDetail?.flight?.passengers : [
          { first_name: "", last_name: "" }
        ]
      },
      expense_accomodation: {
        reservation_number: expenseDetail?.accomodation?.reservation_number || "",
        accomodation_name:  expenseDetail?.accomodation?.accomodation_name || "",
        accomodation_details: expenseDetail?.accomodation?.accomodation_details || "",
        accomodation_address: expenseDetail?.accomodation?.accomodation_address || "",
        accomodation_country_id: defaultCountryIdIndonesia, // Default to Indonesia
        accomodation_province_id: expenseDetail?.accomodation?.province?.id || '',
        accomodation_city_id: expenseDetail?.accomodation?.city?.id || '',
        is_booking_more_than_one_day: expenseDetail?.accomodation?.reservation_date_2 ? true : false,
        reservation_date_1: expenseDetail?.accomodation?.reservation_date_1 || "",
        reservation_date_2: expenseDetail?.accomodation?.reservation_date_2 || "",
        booking_through: expenseDetail?.accomodation?.booking_through || "",
        other_booking_input: expenseDetail?.accomodation?.other_booking_input || "",
        guests: expenseDetail?.accomodation?.guests?.length > 0 ? expenseDetail?.accomodation?.guests : [
          { first_name: "", last_name: "" }
        ]
      },
      expense_train: {
        reservation_number: expenseDetail?.train?.reservation_number || "",
        train_name: expenseDetail?.train?.train_name || "",
        train_details: expenseDetail?.train?.train_details || "",
        train_type: expenseDetail?.train?.train_type || "",
        reservation_date_1: expenseDetail?.train?.reservation_date_1 || "",
        reservation_date_2: expenseDetail?.train?.reservation_date_2 || "",
        booking_through: expenseDetail?.train?.booking_through || "",
        other_booking_input: expenseDetail?.train?.other_booking_input || "",
        passengers: expenseDetail?.train?.passengers?.length > 0 ? expenseDetail?.train?.passengers : [
          { first_name: "", last_name: "" }
        ]
      },
      expense_car_rental: {
        rental_company: expenseDetail?.car_rental?.rental_company || "",
        description: expenseDetail?.car_rental?.description || "",
        car_type: expenseDetail?.car_rental?.car_type || "",
        booking_start: expenseDetail?.car_rental?.booking_start || "",
        booking_end: expenseDetail?.car_rental?.booking_end || "",
        is_booking_more_than_one_day: expenseDetail?.car_rental?.booking_end ? true : false
      },
      expense_pettycash_operational: {
        datetime: expenseDetail?.pettycash_operational?.datetime || "",
        description: expenseDetail?.pettycash_operational?.description || "",
        tags: expenseDetail?.pettycash_operational?.pettycash_tags?.length > 0 ? expenseDetail?.pettycash_operational?.pettycash_tags?.map((val) => ({
          label: val?.tags?.name,
          value: val?.tags?.id
        })) : []
      },
      expense_meals: {
        restaurant_name: expenseDetail?.meal?.restaurant_name || "",
        datetime: expenseDetail?.meal?.datetime || "",
        description: expenseDetail?.meal?.description || ""
      },
      expense_fuel: {
        company_name: expenseDetail?.fuel?.company_name || "",
        other_company_input: expenseDetail?.fuel?.other_company_input || "",
        datetime: expenseDetail?.fuel?.datetime || "",
        description: expenseDetail?.fuel?.description || ""
      },
      expense_etoll: {
        datetime: expenseDetail?.etoll?.datetime || "",
        description: expenseDetail?.etoll?.datetime || ""
      },
      expense_transportation: {
        datetime: expenseDetail?.transportation?.datetime || "",
        transportation_category: expenseDetail?.transportation?.transportation_category || "",
        other_transportation_input: expenseDetail?.transportation?.other_transportation_input || "",
        description: expenseDetail?.transportation?.description || ""
      },
      expense_office_supplies: {
        company: expenseDetail?.office_supply?.company || "",
        datetime: expenseDetail?.office_supply?.datetime || "",
        description: expenseDetail?.office_supply?.description || ""
      },
      expense_printing: {
        company: expenseDetail?.printing?.company || "",
        datetime: expenseDetail?.printing?.datetime || "",
        description: expenseDetail?.printing?.description || ""
      },
      expense_other_reimbursement: {
        reimbursement_for: expenseDetail?.other_reimbursement?.reimbursement_for || "",
        company: expenseDetail?.other_reimbursement?.company || "",
        datetime: expenseDetail?.other_reimbursement?.datetime || "",
        description: expenseDetail?.other_reimbursement?.description || ""
      }
    },
    validationSchema: Yup.object().shape({
      expense_name: Yup.string()
        .required("Please enter expense name."),
      expense_description: Yup.string()
        .required("Please enter expense description."),
      expense_value: Yup.string()
        .required("Please enter expense value."),
      expense_date: Yup.string()
        .when(['expense_repeat_interval'], {
          is: (expense_repeat_interval) => expense_repeat_interval === 'no repeat',
          then: () => Yup.string().required("Please select expense date."),
        }),
      expense_repeat_start_date: Yup.string()
        .when(['expense_repeat_interval'], {
          is: (expense_repeat_interval) => expense_repeat_interval !== 'no repeat',
          then: () => Yup.string().required("Please select expense repeat start date."),
        }),
      expense_repeat_end_date: Yup.string()
        .when(['expense_repeat_forever', 'expense_repeat_interval'], {
          is: (expense_repeat_forever, expense_repeat_interval) => expense_repeat_forever == false && expense_repeat_interval !== 'no repeat',
          then: () => Yup.string().required('Please select expense repeat end date.')
        }),
      expense_repeat_interval: Yup.string()
        .required("Please select expense repeat interval."),
      project_id: Yup.string()
        .when('is_related_to_project', {
          is: true,
          then: () => Yup.string().required('Please select project name')
        }),
      expense_flight: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Flight',
        then: () => Yup.object({
          reservation_number: Yup.string().required('Please enter reservation number'),
          airplane_name: Yup.string().required('Please enter airplane name'),
          flight_details: Yup.string().required('Please enter flight detail'),
          flight_type: Yup.string().required('Please select flight type'),
          reservation_date_1: Yup.string()
          .when("flight_type", {
            is: (flight_type) => flight_type == 'One way' || flight_type == 'Return',
            then: (flight_type) => Yup.string().required(flight_type == 'One way' ? 'Please select flight date' : 'Please select flight departure date'),
          }),
          reservation_date_2: Yup.string()
          .when("flight_type", {
            is: (flight_type) => flight_type == 'Return',
            then: () => Yup.string().required('Please select flight return date')
          }),
          booking_through: Yup.string().required('Please select booking through'),
          other_booking_input: Yup.string().when("booking_through", {
            is: (booking_through) => booking_through == 'Others',
            then: () => Yup.string().required("Please specify")
          }),
          passengers: Yup.array().of(
            Yup.object({
              first_name: Yup.string().required('Please enter first name'),
              last_name: Yup.string().required('Please enter last name')
            })
          ).min(1, 'Please add at least one passenger')
        })
      }),
      expense_accomodation: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Accomodation',
        then: () => Yup.object({
          reservation_number: Yup.string().required('Please enter reservation number'),
          accomodation_name: Yup.string().required('Please enter accomodation name'),
          accomodation_details: Yup.string().required('Please enter accomodation detail'),
          accomodation_address: Yup.string().required('Please enter accomodation address'),
          accomodation_province_id: Yup.string().required('Please select accomodation province'),
          accomodation_city_id: Yup.string().required('Please select accomodation city'),
          is_booking_more_than_one_day: Yup.boolean(),
          reservation_date_1: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true || is_booking_more_than_one_day == false,
            then: (is_booking_more_than_one_day) => Yup.string().required(is_booking_more_than_one_day ? 'Please select reservation start date' : 'Please select reservation date'),
          }),
          reservation_date_2: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true,
            then: () => Yup.string().required('Please select reservation end date')
          }),
          booking_through: Yup.string().required('Please select booking through'),
          other_booking_input: Yup.string().when("booking_through", {
            is: (booking_through) => booking_through == 'Others',
            then: () => Yup.string().required("Please specify")
          }),
          guests: Yup.array().of(
            Yup.object({
              first_name: Yup.string().required('Please enter first name'),
              last_name: Yup.string().required('Please enter last name')
            })
          ).min(1, 'Please add at least one guest')
        })
      }),
      expense_train: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Train',
        then: () => Yup.object({
          reservation_number: Yup.string().required('Please enter reservation number'),
          train_name: Yup.string().required('Please enter train name'),
          train_details: Yup.string().required('Please enter train detail'),
          train_type: Yup.string().required('Please select train type'),
          reservation_date_1: Yup.string()
          .when("train_type", {
            is: (train_type) => train_type == 'One way' || train_type == 'Return' || train_type == '',
            then: (train_type) => Yup.string().required(train_type !== '' ? train_type == 'One way' ? 'Please select train date' : 'Please select train departure date' : 'Please select train date'),
          }),
          reservation_date_2: Yup.string()
          .when("train_type", {
            is: (train_type) => train_type == 'Return',
            then: () => Yup.string().required('Please select train return date')
          }),
          booking_through: Yup.string().required(),
          other_booking_input: Yup.string().when("booking_through", {
            is: (booking_through) => booking_through == 'Others',
            then: () => Yup.string().required("Please specify")
          }),
          passengers: Yup.array().of(
            Yup.object({
              first_name: Yup.string().required('Please enter first name'),
              last_name: Yup.string().required('Please enter last name')
            })
          ).min(1, 'Please add at least one passenger')
        })
      }),
      expense_car_rental: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Car rental',
        then: () => Yup.object({
          rental_company: Yup.string().required('Please enter company'),
          description: Yup.string().required('Please enter description'),
          car_type: Yup.string().required('Please enter car type'),
          is_booking_more_than_one_day: Yup.boolean(),
          booking_start: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true || is_booking_more_than_one_day == false,
            then: (is_booking_more_than_one_day) => Yup.string().required(is_booking_more_than_one_day ? 'Please select booking start date' : 'Please select booking period'),
          }),
          booking_end: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true,
            then: () => Yup.string().required('Please select booking end date')
          }),
        })
      }),
      expense_pettycash_operational: Yup.object().when("category_value", {
        is: (category_value) => {
          category_value?.label == 'Pettycash & Operational'
        },
        then: () => Yup.object({
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description'),
          tags: Yup.array().of(Yup.string().required())
        })
      }),
      expense_meals: Yup.object()
        .when("category_value", {
          is: (category_value) => category_value?.label === 'Meals',
          then: () => Yup.object({
            restaurant_name: Yup.string().required('Please enter restaurant name'),
            datetime: Yup.string().required('Please select date time'),
            description: Yup.string().required('Please enter description')
          })
        }),
      expense_fuel: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Fuel',
        then: () => Yup.object({
          company_name: Yup.string().required('Please enter company name'),
          other_company_input: Yup.string().when("company_name", {
            is: (company_name) => company_name == "Others",
            then: () => Yup.string().required("Please specify")
          }),
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_etoll: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'E-toll money',
        then: () => Yup.object({
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_transportation: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Transportation',
        then: () => Yup.object({
          datetime: Yup.string().required('Please select date time'),
          transportation_category: Yup.string().required('Please select transportation'),
          other_transportation_input: Yup.string().when("transportation_category", {
            is: (transportation_category) => transportation_category == "Others",
            then: () => Yup.string().required("Please specify")
          }),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_office_supplies: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Office supplies / stationery',
        then: () => Yup.object({
          company: Yup.string().required('Please enter company'),
          datetime: Yup.string().required('Please select datetime'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_printing: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Printing',
        then: () => Yup.object({
          company: Yup.string().required('Please enter company'),
          datetime: Yup.string().required('Please select datetime'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_other_reimbursement: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Other reimbursement',
        then: () => Yup.object({
          reimbursement_for: Yup.string().required('Please enter reimbursement for'),
          company: Yup.string().required('Please enter company'),
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description')
        })
      })
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllProject()
    fetchDetailExpense()
    fetchAllExpenseCategories()
    fetchAllExpenseTags()
  }, [id])

  useEffect(() => {
    if (expenseCategoryValue?.label == 'Accomodation') {
      fetchAllProvince()
    }
  }, [expenseCategoryValue])

  const fetchDetailExpense = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/tracker/v1/expense/${id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          let detail = data?.value

          if (detail?.project) {
            setProjectValue({
              label: detail?.project?.name,
              value: detail?.project?.id
            })
          }

          setIntervalValue({
            label: capitalizeFirstLetter(detail?.repeat_interval),
            value: detail?.repeat_interval
          })

          if (detail?.category) {
            let categoryValue = {
              label: detail?.category?.name,
              value: detail?.category?.id
            }
            detail.category_value = categoryValue
            setExpenseCategoryValue(categoryValue)

            if (detail?.category?.name == 'Transportation') {
              setExpenseTransportationValue({
                label: detail?.transportation?.transportation_category,
                value: detail?.transportation?.transportation_category
              })
            }

            if (detail?.category?.name == 'Fuel') {
              setExpenseFuelValue({
                label: detail?.fuel?.company_name,
                value: detail?.fuel?.company_name
              })
            }

            if (detail?.category?.name == 'Flight') {
              setExpenseFlightValue({
                label: detail?.flight?.booking_through,
                value: detail?.flight?.booking_through
              })

              setExpenseAirplaneValue({
                label: detail?.flight?.airplane_name,
                value: detail?.flight?.airplane_name
              })
            }

            if (detail?.category?.name == 'Train') {
              setExpenseTrainValue({
                label: detail?.train?.booking_through,
                value: detail?.train?.booking_through
              })
            }
          }

          setExpenseDetail(detail)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllProject = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: "/api/tracker/v1/project",
    })
      .then(({ data }) => {
        if (data?.success) {
          setProjectOptions(singleProjectsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllExpenseCategories = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/expense/category/option/list",
    })
      .then(({ data }) => {
        if (data?.success) {
          setExpenseCategoryOptions(singleExpenseCategoryOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllExpenseTags = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/expense/tags/option/list",
    })
      .then(({ data }) => {
        if (data?.success) {
          setExpenseTagsOptions(singleExpenseTagsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };
  
  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        if (data?.length > 0) {
          let countryIdIndo = data?.filter((country) => country?.name?.includes('Indonesia'))?.map((val) => val?.id)
          setDefaultCountryIdIndonesia(countryIdIndo)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const fetchAllProvince = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries/provinces",
    })
      .then(({ data }) => {
        if (data) {
          let finalProvinceOptions = data;
          finalProvinceOptions = finalProvinceOptions.map((prov) => {
            let objProv = {
              label: prov.province_name,
              value: prov.province_id,
            };
            return objProv;
          });
          setProvinceOptions(finalProvinceOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllCities = (provinceId) => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/countries/cities/${provinceId}`,
    })
      .then(({ data }) => {
        if (data) {
          let finalCityOptions = data;
          finalCityOptions = finalCityOptions.map((city) => {
            let objCity = {
              label: city.city_name,
              value: city.city_id,
            };
            return objCity;
          });
          setCityOptions(finalCityOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: moment(date).format('YYYY-MM-DD HH:mm:ss')
    }), false)
  }

  const handleInputValue = (values) => {
    const formData = new FormData()

    formData.append('name', values?.expense_name)
    formData.append('description', values?.expense_description)
    formData.append('value', parseFloat(values?.expense_value?.replace(/,/g, "")))
    if (values?.expense_repeat_interval == 'no repeat') {
      formData.append('date', values?.expense_date)
    } else {
      formData.append('repeat_start_date', values?.expense_repeat_start_date)
      formData.append('repeat_end_date', values?.expense_repeat_end_date)
    }
    formData.append('repeat_forever', values.expense_repeat_forever)
    formData.append('repeat_interval', values?.expense_repeat_interval)
    formData.append('notes', values?.expense_notes)
    if (values.file?.length > 0) {
      if (values?.file[0]?.attachment_url) {
        formData.append('attachment_url', values?.file[0]?.attachment_url)
        formData.append('attachment_original_name', values?.file[0]?.attachment_original_name)
      } else {
        formData.append('file', values?.file[0])
      }
    }
    if (values?.is_related_to_project) {
      formData.append('project_id', values?.project_id)
    }
    formData.append('is_update_all', false)
    if (values?.category_id) {
      let type = values?.category_value?.label
      formData.append('category_type', values?.category_value?.label)
      formData.append('category_id', values?.category_id)

      switch (type) {
        case 'Flight':
          if (values?.expense_flight) {
            formData.append('flight', JSON.stringify(values?.expense_flight))
          }
          break;
        case 'Accomodation':
          if (values?.expense_accomodation) {
            formData.append('accomodation', JSON.stringify(values?.expense_accomodation))
          }
          break;
        case 'Train':
          if (values?.expense_train) {
            formData.append('train', JSON.stringify(values?.expense_train))
          }
          break;
        case 'Car rental':
          if (values?.expense_car_rental) {
            formData.append('car_rental', JSON.stringify(values?.expense_car_rental))
          }
          break;
        case 'Pettycash & Operational':
          if (values?.expense_pettycash_operational) {
            formData.append('pettycash_operational', JSON.stringify(values?.expense_pettycash_operational))
          }
          break;
        case 'Meals':
          if (values?.expense_meals) {
            formData.append('meal', JSON.stringify(values?.expense_meals))
          }
          break;
        case 'Fuel':
          if (values?.expense_fuel) {
            formData.append('fuel', JSON.stringify(values?.expense_fuel))
          }
          break;
        case 'E-toll money':
          if (values?.expense_etoll) {
            formData.append('etoll_money', JSON.stringify(values?.expense_etoll))
          }
          break;
        case 'Transportation':
          if (values?.expense_transportation) {
            formData.append('transportation', JSON.stringify(values?.expense_transportation))
          }
          break;
        case 'Office supply / stationery':
          if (values?.expense_office_supplies) {
            formData.append('office_supply', JSON.stringify(values?.expense_office_supplies))
          }
          break;
        case 'Printing':
          if (values?.expense_printing) {
            formData.append('printing', JSON.stringify(values?.expense_printing))
          }
          break;
        case 'Other reimbursement':
          if (values?.expense_other_reimbursement) {
            formData.append('other_reimbursement', JSON.stringify(values?.expense_other_reimbursement))
          }
          break;
        default:
          break;
      }
    }
    
    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/expense/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('Expense has been successfully duplicated')
          navigate('/expense/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSelectProject = (data) => {
    formik.setValues((values) => ({
      ...values,
      project_id: data?.value
    }), false)

    setProjectValue(data)
  }

  const handleSelectExpenseCategory = (data) => {
    formik.setFieldValue("category_id", data?.value);
    formik.setFieldValue("category_value", data);

    setExpenseCategoryValue(data ? data : null)

    if (data) {
      if (data?.label == 'Accomodation') {
        fetchAllCountries()
      }
    }
  }

  const handleSelectExpenseInterval = (data) => {
    formik.setValues((values) => ({
      ...values,
      expense_repeat_interval: data?.value,
      expense_repeat_start_date: data?.value == 'no repeat' ? '' : values.expense_repeat_start_date,
      expense_repeat_end_date: data?.value == 'no repeat' ? '' : values.expense_repeat_end_date,
      expense_date: data?.value == 'no repeat' ? values.expense_date : ''
    }), false)

    setIntervalValue(data)
  }

  const handleFileInput = (values) => {
    const files = values

    if (files) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        file: files
      }), false)
    }
  }

  const handleRemoveFile = () => {
    formik.setValues((prevValues) => ({
      ...prevValues,
      file: []
    }), false)

    setShowDeleteItem({ show: false, item: {} })
  }

  const handleSwitchRepeatForever = (e) => {
    let interval = formik.values.expense_repeat_interval
    
    if (
      formik.values.expense_repeat_interval == 'no repeat' &&
      e.target.checked == true
    ) {
      interval = ''
      setIntervalValue(null)
    }

    formik.setValues((prevValues) => ({
      ...prevValues,
      expense_repeat_forever: e.target.checked,
      expense_repeat_interval: interval
    }), false)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleChangeFieldCategory = (e, category) => {
    const name = e?.target?.name
    const value = e?.target?.value
    const checked = e?.target?.checked

    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [name]: name == 'is_booking_more_than_one_day' ? checked : value
      }
    }), false)
  }

  const handleOnDateChangeCategory = (date, name, category) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [name]: moment(date).format('YYYY-MM-DD HH:mm:ss')
      }
    }), false)
  }

  const handleSelectOptionExpenseCategory = (data, name, category, isMultiple) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [name]: isMultiple ? data?.length > 0 ? data?.map((val) => {
          return {
            label: val?.label,
            value: val?.value
          }
        }) : [] : data ? data?.value : null
      }
    }), false)

    if (category == 'expense_flight') {
      if (name == 'booking_through') {
        setExpenseFlightValue(data)
      }
      if (name == 'airplane_name') {
        setExpenseAirplaneValue(data)
      }
    }

    if (category == 'expense_train') {
      if (name == 'booking_through') {
        setExpenseTrainValue(data)
      }
    }

    if (category == 'expense_transportation') {
      setExpenseTransportationValue(data)
    }

    if (category == 'expense_fuel') {
      setExpenseFuelValue(data)
    }

    if (category == 'expense_accomodation') {
      if (name == 'booking_through') {
        setExpenseAccomodationValue(data)
      }
      if (name == 'accomodation_province_id') {
        fetchAllCities(data?.value)
        setExpenseAccomodationProvinceValue(data)
      }
      if (name == 'accomodation_city_id') {
        setExpenseAccomodationCitiesValue(data)
      }
    }
  }

  const handleChangePassenger = (e, index, category) => {
    const name = e?.target?.name;
    const value = e?.target?.value;

    const path = `${category}.passengers[${index}].[${name}]`;
    
    formik.setFieldValue(path, value, false);
  };

  const handleAddPassenger = (category) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [category == 'expense_accomodation' ? 'guests' : 'passengers']: [
          ...values[category]?.[category == 'expense_accomodation' ? 'guests' : 'passengers'],
          ...[
            {
              first_name: '',
              last_name: ''
            }
          ]
        ]
      }
    }), false)
  }

  const handleRemovePassenger = (index, category) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [category == 'expense_accomodation' ? 'guests' : 'passengers']: values[category]?.[category == 'expense_accomodation' ? 'guests' : 'passengers']?.filter((_, itemIndex) => itemIndex !== index)
      }
    }), false)
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showDeleteItem?.item?.attachment_original_name || showDeleteItem?.item?.name}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to duplicate this expense?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Duplicate Expense</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            onDateChange={handleOnDateChange}
            handleFileInput={handleFileInput}
            projectOptions={projectOptions}
            handleSelectProject={handleSelectProject}
            expenseIntervalOptions={singleExpenseIntervalOptionSelect(formik.values.expense_repeat_forever)}
            handleSelectInterval={handleSelectExpenseInterval}
            handleConfirmDelete={setShowDeleteItem}
            projectValue={projectValue}
            intervalValue={intervalValue}
            handleSwitchRepeatForever={handleSwitchRepeatForever}
            handleViewFile={setOpenFilePreview}
            expenseCategoryOptions={expenseCategoryOptions}
            expenseCategoryValue={expenseCategoryValue}
            expenseTagsOptions={expenseTagsOptions}
            handleSelectExpenseCategory={handleSelectExpenseCategory}
            handleChangeFieldCategory={handleChangeFieldCategory}
            handleOnDateChangeCategory={handleOnDateChangeCategory}
            expenseTransportationOptions={singleExpenseCategoryTransportOptionSelect()}
            expenseFuelOptions={singleExpenseCategoryFuelOptionSelect()}
            handleSelectOptionExpenseCategory={handleSelectOptionExpenseCategory}
            expenseTransportationValue={expenseTransportationValue}
            expenseFuelValue={expenseFuelValue}
            tripTypeOptions={singleExpenseCategoryTripTypeOptionSelect()}
            bookingThroughFlighOptions={singleExpenseCategoryFlightOptionSelect()}
            bookingThroughTrainOptions={singleExpenseCategoryTrainOptionSelect()}
            expenseFlightValue={expenseFlightValue}
            expenseTrainValue={expenseTrainValue}
            handleChangePassenger={handleChangePassenger}
            handleAddPassenger={handleAddPassenger}
            handleRemovePassenger={handleRemovePassenger}
            expenseAirplaneOptions={singleExpenseCategoryAirplaneOptionSelect()}
            expenseAirplaneValue={expenseAirplaneValue}
            bookingThroughAccomodationOptions={singleExpenseCategoryAccomodationOptionSelect()}
            expenseAccomodationValue={expenseAccomodationValue}
            provinceOptions={provinceOptions}
            cityOptions={cityOptions}
            expenseAccomodationProvinceValue={expenseAccomodationProvinceValue}
            expenseAccomodationCitiesValue={expenseAccomodationCitiesValue}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/expense/list`)}
            onSave={() => setShowModalConfirm({ show: true })}
            textConfirm='Duplicate'
          />
        }
      />
    </>
  )
}

export default DuplicateExpense